import React from 'react';
import { Waypoint } from 'react-waypoint';
import { ElementLoader } from 'ui';
import { ComplexValue } from 'app/types-shared';
import { OverlayComponent } from 'app/components';
import { deepEquals } from 'app/infrastructure/js';
import { SearchFilters } from 'app/area/search-filters/search-filters';
import { SerializePayload } from 'app/area/search-filters/types';
import { TextConfigurationTranslation } from '../../../constants';
import { ExceededLengthError } from '../../common/exceeded-length-error';
import { ListHeader } from '../../common/list-header';
import { ModifiedCodeDescription, CodeDescriptionListState, CodeDescriptionSearchModel } from '../types-store';
import { codeFilters, defaultFilters, predefinedFilters } from '../code-filter-configuration';
import { excelExportFilename } from '../constants';
import { serialize } from '../serialize';
import { CodeDescriptionResults } from './code-description-results';
import { CodeDescriptionToolbar } from './code-description-toolbar';

interface CodeDescriptionRendererProps {
  listState: CodeDescriptionListState;

  loadTextsList: (model: CodeDescriptionSearchModel, startIndex: number) => void;
  modifyText: (modifiedCodeDescription: ModifiedCodeDescription) => void;
  cancelChanges: () => void;
  saveChanges: (modifiedCodeDescriptions: ModifiedCodeDescription[],
    successMessage: string,
    validationErrorMessage: string) => void;

  exportToExcel: (
    model: CodeDescriptionSearchModel,
    maxResults: number,
    filename: string,
    startMessage: string,
    successMessage: string
  ) => void;
  openExcelImport: () => void;

  translation: TextConfigurationTranslation;

  language: ComplexValue<number>;
}

// eslint:disable-next-line: max-func-body-length
export const CodeDescriptionRenderer: React.FC<CodeDescriptionRendererProps> = ({
  listState, loadTextsList, modifyText, cancelChanges, saveChanges,
  translation, exportToExcel, openExcelImport, language
}) => {

  const { metadata, listItems, modifiedItems, numberOfResults, exportExcelFetching, importExcelFetching,
    requestedSearchModel, fetching, updating, exceededLengthError, validation } = listState;

  const updateSearchModel = (payload: SerializePayload) => {
    onLoadData(serialize(payload));
  };

  const onLoadData = (searchModel: CodeDescriptionSearchModel, startIndex?: number) => {

    if (startIndex !== undefined || !deepEquals(searchModel, requestedSearchModel)) {
      loadTextsList(searchModel, startIndex);
    }
  };

  const onFetchMore = () => {

    if (fetching || !listItems || listItems.length === 0 || listItems.length === numberOfResults) {
      return;
    }

    onLoadData(requestedSearchModel, listItems.length);
  };

  const onSaveChanges = () => {
    saveChanges(modifiedItems,
      translation.message_description_updated,
      translation.text_validation_error);
  };

  const onExportToExcel = () => {
    listItems && listItems.length > 0 && exportToExcel(
      requestedSearchModel,
      numberOfResults,
      excelExportFilename,
      translation.excel_started,
      translation.excel_success
    );
  };

  const topToolbar = <CodeDescriptionToolbar
    onExportToExcel={onExportToExcel}
    openExcelImport={openExcelImport}
    exportExcelFetching={exportExcelFetching}
    importExcelFetching={importExcelFetching}
    totalResultHits={numberOfResults}
    translation={translation}
  />;

  const changedItems = (modifiedItems || []).filter(code => !code.onlyWhitespace);
  const allValid = (validation?.text?.messages?.length ?? 0) === 0 &&
    !changedItems.some(code => code.description.trim() === '');

  return <>
    <OverlayComponent
      displayOverlay={updating}
      overlayText={translation.saving_texts} />
    <SearchFilters
      topToolbar={topToolbar}
      hideSearch={true}
      displayInfoText={true}
      filtersConfig={{ area: 'codeDescription' }}
      queryFilters={undefined}
      predefinedFilters={predefinedFilters(translation)}
      customFilters={codeFilters(translation)}
      defaultFilterValues={defaultFilters(translation, language)}
      updateSearchModel={updateSearchModel}>
      <ExceededLengthError
        errors={exceededLengthError}
        hint={translation.code_description_exceeded_length_hint} />
      {!!listItems && <ListHeader
        numberOfChanges={changedItems.length}
        numberOfResults={numberOfResults}
        allValid={allValid}
        cancelChanges={cancelChanges}
        saveChanges={onSaveChanges}
        updateLabel={translation.update_descriptions}
        modifiedLabel={translation.modified_descriptions}
        noHitsLabel={translation.search_no_description}
        translation={translation} />}
      {listItems && listItems.length > 0
        ? <CodeDescriptionResults
          metadata={metadata}
          textsList={listItems}
          modifiedTexts={modifiedItems}
          modifyText={modifyText}
          validation={validation}
          translation={translation} />
        : null}
      <Waypoint key="wpCodeDescription" onEnter={onFetchMore} scrollableAncestor={window} bottomOffset="-10%" />
      {listState.fetching ? <ElementLoader /> : null}
    </SearchFilters>
  </>;
};
